import { useEffect } from 'react';
import { LuX } from 'react-icons/lu';
import { IconButton } from '../design-system/components';
import { usePromptDialog } from '../hooks/usePromptDialog';
import { trackGaEvent } from '../utils/ga';
import type { GaCreditFeature } from '../utils/gaCreditFeature';
import OutOfTokensPrompt from './OutOfTokensPrompt';
import type { Usage } from '../types';
import { spendableCredits } from '../utils/spendable-credits';

interface OutOfTokensModalProps {
  isOpen: boolean;
  onClose: () => void;
  usage?: Usage | null;
  tokenType?: string;
  /**
   * The surface this modal is on, for the GA `out_of_credits` event: the
   * same value the global prompt reports for that surface's server refusal
   * (utils/gaCreditFeature.ts), so one feature means one surface. Required,
   * so a new caller cannot fall into misc_ai by omission. Separate from
   * tokenType, which picks the legacy token bucket to display.
   */
  gaFeature: GaCreditFeature;
}

/**
 * Modal wrapper for OutOfTokensPrompt
 * Provides consistent modal UI across all AI features.
 * Styles: design-system/pages/prompts.css (`.ee-prompt-modal`).
 * Prefers the unified credits system when available, falls back to legacy token buckets.
 */
export default function OutOfTokensModal({
  isOpen,
  onClose,
  usage,
  tokenType = 'misc_ai',
  gaFeature,
}: OutOfTokensModalProps) {
  // THE monetization event (GA-INSTRUMENTATION.md phase 2): which feature's
  // credit exhaustion creates upgrade pressure. Joined against
  // begin_checkout/subscribe it gives per-trigger paywall conversion.
  //
  // A server 402 the interceptor answers is counted by the global prompt as
  // it opens (stores/upgrade-prompt.store.ts, review round 2 q10). This modal
  // opens only where that prompt does not: a page's pre-check (credits
  // already 0, no request sent) and a 402 without the structured payload.
  // So each refusal is counted once, by whichever dialog answers it.
  useEffect(() => {
    if (isOpen) trackGaEvent('out_of_credits', { feature: gaFeature });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Why: fire once per OPEN, not on gaFeature churn
  }, [isOpen]);

  // The app's modal keyboard contract (redesign PR23): focus moves in on
  // open, Tab stays inside, Escape closes like the close button, and focus
  // returns to whatever had it when the modal opened. A gate the interceptor
  // raised over this (the 402 / 403 prompt, the plan selector) is the top
  // layer until it is gone (usePromptDialog).
  const { titleId, dialogRef } = usePromptDialog(isOpen, onClose);

  if (!isOpen) return null;

  const credits = usage?.credits;
  const tokenBucket = usage?.tokens?.[tokenType];

  // Credits-first: use unified credits when available, fall back to legacy tokens
  const info = credits
    ? {
        tokens_available: spendableCredits(usage) ?? 0,
        tokens_limit: credits.limit,
        tokens_used: credits.used,
      }
    : {
        tokens_available: tokenBucket?.remaining || 0,
        tokens_limit: tokenBucket?.limit || 0,
        tokens_used: tokenBucket?.used || 0,
      };

  return (
    // Raised: above the support-chat panel it can open from, and the nav.
    <div className="ee-prompt ee-prompt-modal ee-prompt-modal--raised">
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className="ee-prompt-dialog"
      >
        {/* Close button: sticky in the scrolling dialog, so it never scrolls away */}
        <IconButton
          label="Close"
          onClick={onClose}
          className="ee-prompt-dialog__close"
          icon={<LuX />}
        />

        <OutOfTokensPrompt
          tokenInfo={info}
          tokenType={tokenType}
          useCredits={!!credits}
          titleId={titleId}
        />
      </div>
    </div>
  );
}
